import { hotRanking, topicTags } from '@/mocks/homeData';

const TREND_MAP: Record<string, { icon: string; className: string; label: string }> = {
  up: { icon: 'ri-arrow-up-line', className: 'text-primary-600 bg-primary-100', label: '上升' },
  down: { icon: 'ri-arrow-down-line', className: 'text-secondary-700 bg-secondary-100', label: '回落' },
  flat: { icon: 'ri-subtract-line', className: 'text-foreground-600 bg-background-200', label: '持平' },
};

function formatHeat(heat: number) {
  return `${(heat / 10000).toFixed(1)} 万`;
}

export default function RankingSection() {
  const topThree = hotRanking.slice(0, 3);
  const restList = hotRanking.slice(3);

  return (
    <section id="ranking" className="w-full border-t border-background-200 bg-background-100 py-10 md:py-14">
      <div className="mx-auto w-full max-w-[1360px] px-4 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px] lg:gap-10">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-600">
              Hot Ranking
            </span>
            <h2 className="mt-2 font-heading text-xl font-black tracking-tight text-foreground-950 md:text-3xl">
              吃瓜热榜 · 今天谁在榜上
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              按讨论量、转发量与评论增速实时合成。黑料六点每日定时刷新，榜位变化也能当线索看。
            </p>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {topThree.map((item, index) => (
                <div
                  key={item.rank}
                  className={`flex flex-col rounded-lg border p-4 ${
                    index === 0
                      ? 'border-primary-300 bg-primary-50'
                      : 'border-background-200 bg-background-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-md font-heading text-sm font-black ${
                        index === 0
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-200 text-foreground-800'
                      }`}
                    >
                      {item.rank}
                    </span>
                    <span
                      className={`flex h-6 items-center gap-1 rounded-sm px-1.5 text-[11px] font-semibold ${
                        TREND_MAP[item.trend].className
                      }`}
                    >
                      <i className={TREND_MAP[item.trend].icon} />
                      {TREND_MAP[item.trend].label}
                    </span>
                  </div>
                  <p className="mt-3 line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-950">
                    {item.title}
                  </p>
                  <span className="mt-3 flex items-center gap-1.5 text-xs text-foreground-500">
                    <i className="ri-fire-line text-sm text-primary-500" />
                    热度 {formatHeat(item.heat)}
                  </span>
                </div>
              ))}
            </div>

            <ul className="mt-4 divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {restList.map((item) => (
                <li
                  key={item.rank}
                  className="group flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-background-100"
                >
                  <span className="w-6 shrink-0 font-heading text-sm font-black tabular-nums text-foreground-400">
                    {String(item.rank).padStart(2, '0')}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                    {item.title}
                  </span>
                  <span className="hidden shrink-0 text-xs tabular-nums text-foreground-500 sm:block">
                    {formatHeat(item.heat)}
                  </span>
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-xs ${
                      TREND_MAP[item.trend].className
                    }`}
                  >
                    <i className={TREND_MAP[item.trend].icon} />
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                  <i className="ri-price-tag-3-line text-base" />
                </span>
                <p className="font-heading text-sm font-black text-foreground-950">话题标签</p>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                标签跟着当天的讨论一起长，点进去能看到同一话题下的全部条目。
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {topicTags.map((tag) => (
                  <button
                    key={tag.name}
                    type="button"
                    className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      tag.hot
                        ? 'border-accent-300 bg-accent-100 text-accent-900 hover:bg-accent-200'
                        : 'border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-300 hover:bg-secondary-50'
                    }`}
                  >
                    {tag.hot && <i className="ri-fire-fill text-[11px] text-primary-500" />}
                    {tag.name}
                    <span className="text-[11px] text-foreground-500">{tag.count}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-foreground-950 p-5">
              <p className="font-heading text-sm font-black text-background-50">今日一句话</p>
              <p className="mt-2 text-sm leading-relaxed text-background-200">
                「六点前存疑，六点后回应。」今天几乎所有条目都在验证这句话。
              </p>
              <div className="mt-4 flex items-center gap-2 text-xs text-background-300">
                <i className="ri-quill-pen-line" />
                编辑部 · 18:24 记
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}